import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { supabase } from '../lib/supabase'
import { captureAppException, setMonitoringUser } from '../lib/monitoring'
import { getPasswordResetRedirectUrl, getSignupConfirmationRedirectUrl } from '../utils/appUrl'
import { inferProductTypeFromName } from '../utils/products'
import type {
  AgencySignupRequest,
  Allocation,
  BankAccount,
  Db,
  Deal,
  DealStatus,
  Mail,
  OperationLog,
  PayoutRequest,
  Product,
  Supplier,
  SystemSettings,
  Transaction,
  User,
  UserRole,
} from './types'

// ---- helpers ----

function safeEmail(email: string) {
  return email.trim().toLowerCase()
}

async function withTimeout<T>(
  promise: Promise<T>,
  timeoutMs: number,
  timeoutMessage: string,
): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined
  const timeoutPromise = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error(timeoutMessage)), timeoutMs)
  })

  try {
    return await Promise.race([promise, timeoutPromise])
  } finally {
    if (timer) clearTimeout(timer)
  }
}

// DB 竊・Frontend 縺ｮ蝙句､画鋤繝倥Ν繝代・
function mapProfile(row: any): User {
  return {
    id: row.id,
    role: row.role as UserRole,
    name: row.name,
    email: row.email ?? '',
    passwordHash: '', // Supabase Auth 縺檎ｮ｡逅・
    createdAt: row.created_at ?? '',
    deletedAt: row.deleted_at ?? undefined,
    deletedBy: row.deleted_by ?? undefined,
    deleteReason: row.delete_reason ?? undefined,
    bankAccount: row.bank_account ?? undefined,
    agencyRateOverride:
      row.agency_rate_override === null || row.agency_rate_override === undefined
        ? undefined
        : Number(row.agency_rate_override),
    connectorRateOverride:
      row.connector_rate_override === null || row.connector_rate_override === undefined
        ? undefined
        : Number(row.connector_rate_override),
    agency:
      row.invite_code
        ? {
            inviteCode: row.invite_code,
            superAgencyId: row.super_agency_id ?? undefined,
          }
        : undefined,
    connector:
      row.agency_id
        ? { agencyId: row.agency_id, introducedById: row.introduced_by_id ?? undefined }
        : undefined,
  }
}

function mapSupplier(row: any): Supplier {
  return { id: row.id, name: row.name, note: row.note ?? undefined }
}

function mapProduct(row: any): Product {
  return {
    id: row.id,
    supplierId: row.supplier_id,
    name: row.name,
    category: row.category ?? undefined,
    type: row.type ?? inferProductTypeFromName(row.name ?? ''),
    priceLabel: row.price_label ?? undefined,
    listPriceJPY: row.list_price_jpy ?? 0,
    imageUrl: row.image_url ?? undefined,
    description: row.description ?? '',
    materials: row.materials ?? [],
    isPublic: row.is_public,
    agencyRate: row.agency_rate == null ? undefined : Number(row.agency_rate),
    connectorRate: row.connector_rate == null ? undefined : Number(row.connector_rate),
    adSpec: row.ad_spec ?? undefined,
    vacancyStatus: row.vacancy_status ?? undefined,
  }
}

function mapDeal(row: any): Deal {
  return {
    id: row.id,
    createdAt: row.created_at,
    locked: row.locked,
    status: row.status as DealStatus,
    connectorId: row.connector_id ?? undefined,
    assignedAgencyId: row.assigned_agency_id ?? undefined,
    productId: row.product_id,
    customerCompanyName: row.customer_company_name,
    customerName: row.customer_name,
    customerEmail: row.customer_email,
    customerPhone: row.customer_phone ?? undefined,
    memo: row.memo ?? undefined,
    source: row.source,
    finalSaleAmountJPY: row.final_sale_amount_jpy ?? undefined,
    closingDate: row.closing_date ?? undefined,
  }
}

function mapAllocation(row: any): Allocation {
  if (row.recipient_type === 'Jnavi取り分') {
    return {
      id: row.id,
      recipientType: 'Jnavi取り分',
      label: row.label,
      amountJPY: row.amount_jpy,
    }
  }
  return {
    id: row.id,
    recipientType: 'ユーザー報酬',
    userId: row.user_id,
    userRole: row.user_role,
    label: row.label,
    rate: Number(row.rate),
    baseAmountJPY: row.base_amount_jpy,
    amountJPY: row.amount_jpy,
    status: row.status,
    payoutRequestId: row.payout_request_id ?? undefined,
  }
}

function mapTransaction(row: any, allocations: Allocation[]): Transaction {
  const snapshot = row.product_snapshot ?? {}
  return {
    id: row.id,
    createdAt: row.created_at,
    dealId: row.deal_id,
    closingDate: row.closing_date,
    productSnapshot: {
      productId: snapshot.productId ?? snapshot.product_id ?? '',
      name: snapshot.name ?? '',
      category: snapshot.category ?? undefined,
      type: snapshot.type ?? inferProductTypeFromName(snapshot.name ?? ''),
      supplierId: snapshot.supplierId ?? snapshot.supplier_id ?? '',
      listPriceJPY: snapshot.listPriceJPY ?? snapshot.list_price_jpy ?? 0,
      priceLabel: snapshot.priceLabel ?? snapshot.price_label ?? undefined,
    },
    connectorId: row.connector_id ?? undefined,
    agencyId: row.agency_id,
    superAgencyId: row.super_agency_id ?? undefined,
    saleAmountJPY: row.sale_amount_jpy,
    baseAmountJPY: row.base_amount_jpy,
    ratesUsed: row.rates_used,
    superAgencyRewardJPY: row.super_agency_reward_jpy ?? undefined,
    agencyRewardJPY: row.agency_reward_jpy,
    connectorRewardJPY: row.connector_reward_jpy,
    jnaviShareJPY: row.jnavi_share_jpy,
    allocations,
  }
}

function mapPayoutRequest(row: any): PayoutRequest {
  return {
    id: row.id,
    userId: row.user_id,
    requestedAt: row.requested_at,
    amountJPY: row.amount_jpy,
    status: row.status,
    allocationIds: [], // 蛻･騾碑ｧ｣豎ｺ
    processedAt: row.processed_at ?? undefined,
  }
}

function mapMail(row: any): Mail {
  return {
    id: row.id,
    to: row.to_address,
    subject: row.subject,
    body: row.body,
    sentAt: row.sent_at,
  }
}

function mapLog(row: any): OperationLog {
  return {
    id: row.id,
    at: row.at,
    actorUserId: row.actor_user_id,
    action: row.action,
    detail: row.detail,
    relatedId: row.related_id ?? undefined,
  }
}

function mapAgencySignupRequest(row: any): AgencySignupRequest {
  return {
    id: row.id,
    createdAt: row.created_at,
    companyName: row.company_name,
    contactName: row.contact_name,
    email: row.email,
    phone: row.phone ?? undefined,
    website: row.website ?? undefined,
    note: row.note ?? undefined,
    superAgencyId: row.super_agency_id ?? undefined,
    status: row.status,
    reviewedAt: row.reviewed_at ?? undefined,
    reviewedBy: row.reviewed_by ?? undefined,
    reviewNote: row.review_note ?? undefined,
    issuedUserId: row.issued_user_id ?? undefined,
    issuedAt: row.issued_at ?? undefined,
  }
}

// ---- Context ----

type ActionResult = { ok: boolean; message?: string }
type ActionResultWithId<K extends string> = ActionResult & Partial<Record<K, string>>
type RegisterConnectorResult = ActionResult & { needsEmailConfirmation?: boolean }

type DbContextValue = {
  db: Db | null
  loading: boolean
  actions: {
    resetAll: () => Promise<void>

    // Auth
    login: (email: string, password: string) => Promise<ActionResult>
    logout: () => Promise<void>
    requestPasswordReset: (email: string) => Promise<ActionResult>
    resetPassword: (token: string, newPassword: string) => Promise<ActionResult>
    changePassword: (currentPassword: string, newPassword: string) => Promise<ActionResult>

    // Profile
    updateMyProfile: (payload: {
      name: string
      email: string
      bankAccount?: BankAccount
    }) => Promise<ActionResult>

    // Signup (connector)
    registerConnector: (payload: {
      agencyId: string
      introducedById?: string
      name: string
      email: string
      password: string
    }) => Promise<RegisterConnectorResult>
    submitAgencySignupRequest: (payload: {
      companyName: string
      contactName: string
      email: string
      phone: string
      superAgencyId: string
      website?: string
      note?: string
    }) => Promise<ActionResult>

    // Admin: org
    adminSetConnectorAgency: (
      connectorUserId: string,
      agencyUserId: string,
    ) => Promise<ActionResult>
    adminSetCommissionRates: (
      superAgencyRate: number,
      agencyRate: number,
      connectorRate: number,
    ) => Promise<ActionResult>
    adminPromoteAgencyToSuperAgency: (agencyUserId: string) => Promise<ActionResult>
    adminSetAgencySuperAgency: (
      agencyUserId: string,
      superAgencyId?: string,
    ) => Promise<ActionResult>
    adminSetUserCommissionOverride: (payload: {
      userId: string
      agencyRateOverride?: number | null
      connectorRateOverride?: number | null
    }) => Promise<ActionResult>
    adminDeletePartnerAccount: (userId: string) => Promise<ActionResult>
    adminReviewAgencySignupRequest: (
      requestId: string,
      decision: '承認' | '却下',
      reviewNote?: string,
    ) => Promise<ActionResult>
    adminIssueAgencyAccount: (requestId: string) => Promise<
      ActionResult & {
        agencyUserId?: string
        email?: string
        inviteCode?: string
      }
    >
    adminSendAgencyApprovalNotification: (requestId: string) => Promise<ActionResult>

    // Product management
    createProduct: (
      payload: Omit<Product, 'id'>,
    ) => Promise<ActionResultWithId<'productId'>>
    updateProduct: (
      productId: string,
      patch: Partial<Omit<Product, 'id' | 'supplierId'>>,
    ) => Promise<ActionResult>
    deleteProduct: (productId: string) => Promise<ActionResult>

    // Deals
    createDealFromReferral: (payload: {
      connectorId: string
      productId: string
      customerCompanyName: string
      customerName: string
      customerEmail: string
      customerPhone?: string
      memo?: string
    }) => Promise<ActionResultWithId<'dealId'>>

    createDealManual: (payload: {
      productId: string
      customerCompanyName: string
      customerName: string
      customerEmail: string
      customerPhone?: string
      memo?: string
      assignedAgencyId?: string
    }) => Promise<ActionResultWithId<'dealId'>>

    updateDealStatus: (dealId: string, status: DealStatus) => Promise<ActionResult>

    finalizeDeal: (payload: {
      dealId: string
      finalSaleAmountJPY: number
      closingDate: string
    }) => Promise<ActionResultWithId<'transactionId'>>

    // Rewards & payouts
    adminConfirmRewardsForTransaction: (transactionId: string) => Promise<ActionResult>
    requestPayoutAll: () => Promise<ActionResultWithId<'payoutRequestId'>>
    adminMarkPayoutPaid: (payoutRequestId: string) => Promise<ActionResult>
  }
}

const DbContext = createContext<DbContextValue | null>(null)

// ---- Provider ----

export function DbProvider(props: { children: React.ReactNode }) {
  const [db, setDb] = useState<Db | null>(null)
  const [loading, setLoading] = useState(true)
  const [sessionUserId, setSessionUserId] = useState<string | null>(null)

  // 繝・・繧ｿ蜈ｨ隱ｭ縺ｿ霎ｼ縺ｿ
  const loadAllData = useCallback(async (userId: string | null) => {
    setLoading(true)
    try {
      if (!userId) {
        const [profilesRes, suppliersRes, productsRes, settingsRes] = await withTimeout(
          Promise.all([
            supabase.from('profiles').select('id, role, name, invite_code'),
            supabase.from('suppliers').select('*'),
            supabase.from('products').select('*').eq('is_public', true),
            supabase.from('system_settings').select('*').eq('id', 1).single(),
          ]),
          20_000,
          'loadAllData anonymous timeout',
        )

        const users = (profilesRes.data ?? []).map(mapProfile)
        const suppliers = (suppliersRes.data ?? []).map(mapSupplier)
        const products = (productsRes.data ?? []).map(mapProduct)
        const settings: SystemSettings = settingsRes.data
          ? {
              minPayoutJPY: settingsRes.data.min_payout_jpy,
              superAgencyRate: Number(settingsRes.data.super_agency_rate ?? 0.25),
              agencyRate: Number(settingsRes.data.agency_rate),
              connectorRate: Number(settingsRes.data.connector_rate),
            }
          : { minPayoutJPY: 5000, superAgencyRate: 0.25, agencyRate: 0.15, connectorRate: 0.05 }

        setMonitoringUser(null)
        setDb({
          schemaVersion: 5,
          sessionUserId: null,
          users,
          agencySignupRequests: [],
          suppliers,
          products,
          deals: [],
          transactions: [],
          payoutRequests: [],
          settings,
          outbox: [],
          passwordResets: [],
          logs: [],
        })
        return
      }

      const [
        profilesRes,
        suppliersRes,
        productsRes,
        dealsRes,
        transactionsRes,
        allocationsRes,
        payoutsRes,
        settingsRes,
        outboxRes,
        requestsRes,
        logsRes,
      ] = await withTimeout(
        Promise.all([
          supabase.from('profiles').select('*'),
          supabase.from('suppliers').select('*'),
          supabase.from('products').select('*'),
          supabase.from('deals').select('*').order('created_at', { ascending: false }),
          supabase.from('transactions').select('*').order('created_at', { ascending: false }),
          supabase.from('allocations').select('*'),
          supabase.from('payout_requests').select('*').order('requested_at', { ascending: false }),
          supabase.from('system_settings').select('*').eq('id', 1).single(),
          supabase.from('outbox').select('*').order('sent_at', { ascending: false }),
          supabase.from('agency_signup_requests').select('*').order('created_at', { ascending: false }),
          supabase.from('operation_logs').select('*').order('at', { ascending: false }).limit(5000),
        ]),
        20_000,
        'loadAllData authenticated timeout',
      )

      const users = (profilesRes.data ?? []).map(mapProfile)
      const suppliers = (suppliersRes.data ?? []).map(mapSupplier)
      const products = (productsRes.data ?? []).map(mapProduct)
      const deals = (dealsRes.data ?? []).map(mapDeal)

      // allocations 繧偵ヨ繝ｩ繝ｳ繧ｶ繧ｯ繧ｷ繝ｧ繝ｳ縺斐→縺ｫ繧ｰ繝ｫ繝ｼ繝怜喧
      const allocsByTx = new Map<string, Allocation[]>()
      for (const row of allocationsRes.data ?? []) {
        const txId = row.transaction_id
        if (!allocsByTx.has(txId)) allocsByTx.set(txId, [])
        allocsByTx.get(txId)!.push(mapAllocation(row))
      }

      const transactions = (transactionsRes.data ?? []).map((row: any) =>
        mapTransaction(row, allocsByTx.get(row.id) ?? []),
      )

      // payoutRequests 縺ｫ allocationIds 繧剃ｻ倅ｸ・
      const allAllocations = allocationsRes.data ?? []
      const payoutRequests: PayoutRequest[] = (payoutsRes.data ?? []).map((row: any) => ({
        ...mapPayoutRequest(row),
        allocationIds: allAllocations
          .filter((a: any) => a.payout_request_id === row.id)
          .map((a: any) => a.id),
      }))

      const settings: SystemSettings = settingsRes.data
        ? {
            minPayoutJPY: settingsRes.data.min_payout_jpy,
            superAgencyRate: Number(settingsRes.data.super_agency_rate ?? 0.25),
            agencyRate: Number(settingsRes.data.agency_rate),
            connectorRate: Number(settingsRes.data.connector_rate),
          }
        : { minPayoutJPY: 5000, superAgencyRate: 0.25, agencyRate: 0.15, connectorRate: 0.05 }

      const outbox = (outboxRes.data ?? []).map(mapMail)
      const agencySignupRequests = (requestsRes.data ?? []).map(mapAgencySignupRequest)
      const logs = (logsRes.data ?? []).map(mapLog)
      const sessionUser = users.find((u) => u.id === userId)

      setMonitoringUser(
        sessionUser
          ? { id: sessionUser.id, role: sessionUser.role, email: sessionUser.email }
          : { id: userId, role: 'unknown' },
      )

      setDb({
        schemaVersion: 5,
        sessionUserId: userId,
        users,
        agencySignupRequests,
        suppliers,
        products,
        deals,
        transactions,
        payoutRequests,
        settings,
        outbox,
        passwordResets: [], // Supabase Auth 縺檎ｮ｡逅・
        logs,
      })
    } catch (err) {
      captureAppException(err, {
        tags: {
          screen: 'DbProvider.loadAllData',
          user_role: userId ? 'authenticated' : 'anonymous',
        },
      })
      console.error('繝・・繧ｿ隱ｭ縺ｿ霎ｼ縺ｿ繧ｨ繝ｩ繝ｼ:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  // Auth state listener
  useEffect(() => {
    // 迴ｾ蝨ｨ縺ｮ繧ｻ繝・す繝ｧ繝ｳ繝√ぉ繝・け
    supabase.auth.getSession().then(({ data: { session } }) => {
      const uid = session?.user?.id ?? null
      setSessionUserId(uid)
      if (!uid) setMonitoringUser(null)
      loadAllData(uid)
    })

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      const uid = session?.user?.id ?? null
      setSessionUserId(uid)
      if (uid) {
        loadAllData(uid)
      } else {
        // 繝ｭ繧ｰ繧｢繧ｦ繝亥ｾ後・蜈ｬ髢九ョ繝ｼ繧ｿ縺ｮ縺ｿ蜀崎ｪｭ縺ｿ霎ｼ縺ｿ
        setMonitoringUser(null)
        loadAllData(null)
      }
    })

    return () => subscription.unsubscribe()
  }, [loadAllData])

  // 繝ｪ繝輔Ξ繝・す繝･繝倥Ν繝代・
  const refresh = useCallback(() => loadAllData(sessionUserId), [loadAllData, sessionUserId])

  const actions = useMemo<DbContextValue['actions']>(() => {
    return {
      // --- Reset ---
      async resetAll() {
        // 譛ｬ逡ｪ縺ｧ縺ｯ辟｡蜉ｹ蛹匁耳螂ｨ
        console.warn('resetAll is not available in production mode')
      },

      // --- Auth ---
      async login(email, password) {
        let authResult: Awaited<ReturnType<typeof supabase.auth.signInWithPassword>>
        try {
          authResult = await withTimeout(
            supabase.auth.signInWithPassword({
              email: safeEmail(email),
              password,
            }),
            20_000,
            'Supabase auth timeout',
          )
        } catch (error) {
          captureAppException(error, {
            tags: {
              screen: 'DbProvider.actions.login',
            },
          })
          return { ok: false, message: 'ログイン処理に失敗しました。時間をおいて再度お試しください。' }
        }

        const { error } = authResult
        if (error) {
          const msg = error.message?.includes('Invalid login credentials')
            ? 'メールまたはパスワードが正しくありません'
            : `ログインに失敗しました（${error.message}）`
          return { ok: false, message: msg }
        }
        return { ok: true }
      },

      async logout() {
        await supabase.auth.signOut()
      },

      async requestPasswordReset(email) {
        const { error } = await supabase.auth.resetPasswordForEmail(safeEmail(email), {
          redirectTo: getPasswordResetRedirectUrl(),
        })
        if (error) {
          console.error('Password reset error:', error)
        }
        // 繧ｻ繧ｭ繝･繝ｪ繝・ぅ荳・ 蟶ｸ縺ｫ ok 霑泌唆
        return { ok: true }
      },

      async resetPassword(_token, newPassword) {
        if (newPassword.length < 8) {
          return { ok: false, message: 'パスワードは8文字以上にしてください' }
        }
        const { error } = await supabase.auth.updateUser({ password: newPassword })
        if (error) return { ok: false, message: error.message }
        return { ok: true }
      },

      async changePassword(currentPassword, newPassword) {
        if (newPassword.length < 8) {
          return { ok: false, message: 'パスワードは8文字以上にしてください' }
        }

        // 迴ｾ蝨ｨ縺ｮ繝代せ繝ｯ繝ｼ繝峨ｒ讀懆ｨｼ・医そ繝・す繝ｧ繝ｳ逶鈴屮譎ゅ・繝ｪ繧ｹ繧ｯ霆ｽ貂幢ｼ・
        const { data: { user } } = await supabase.auth.getUser()
        if (!user?.email) return { ok: false, message: 'ログインが必要です' }

        const { error: verifyError } = await supabase.auth.signInWithPassword({
          email: user.email,
          password: currentPassword,
        })
        if (verifyError) return { ok: false, message: '現在のパスワードが正しくありません' }

        const { error } = await supabase.auth.updateUser({ password: newPassword })
        if (error) return { ok: false, message: error.message }
        return { ok: true }
      },

      // --- Profile ---
      async updateMyProfile(payload) {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) return { ok: false, message: 'ログインが必要です' }

        if (!payload.name.trim()) return { ok: false, message: '氏名を入力してください' }
        if (!payload.email.trim()) return { ok: false, message: 'メールアドレスを入力してください' }

        const { error } = await supabase
          .from('profiles')
          .update({
            name: payload.name.trim(),
            email: safeEmail(payload.email),
            bank_account: payload.bankAccount ?? null,
          })
          .eq('id', user.id)

        if (error) {
          if (error.code === '23505') return { ok: false, message: 'このメールアドレスは既に使用されています' }
          return { ok: false, message: error.message }
        }

        // Supabase Auth 縺ｮ繝｡繝ｼ繝ｫ繧よ峩譁ｰ
        if (safeEmail(payload.email) !== safeEmail(user.email ?? '')) {
          await supabase.auth.updateUser({ email: safeEmail(payload.email) })
        }

        await refresh()
        return { ok: true }
      },

      // --- Signup (Connector) ---
      async registerConnector(payload) {
        const name = payload.name.trim()
        const email = safeEmail(payload.email)
        const password = payload.password

        if (!name) return { ok: false, message: '氏名を入力してください' }
        if (!email) return { ok: false, message: 'メールアドレスを入力してください' }
        if (password.length < 8) return { ok: false, message: 'パスワードは8文字以上にしてください' }

        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: getSignupConfirmationRedirectUrl(),
            data: {
              name,
              agency_id: payload.agencyId,
              introduced_by_id: payload.introducedById ?? '',
            },
          },
        })

        if (error) {
          if (
            error.code === 'user_already_exists' ||
            error.code === 'email_exists' ||
            error.message.includes('already registered')
          ) {
            return {
              ok: false,
              message: 'このメールアドレスは既に登録されています。ログインまたはパスワード再設定をご利用ください。',
            }
          }
          return { ok: false, message: error.message }
        }

        return { ok: true, needsEmailConfirmation: !data.session }
      },

      async submitAgencySignupRequest(payload) {
        const companyName = payload.companyName.trim()
        const contactName = payload.contactName.trim()
        const email = safeEmail(payload.email)
        const phone = payload.phone.trim()
        const superAgencyId = payload.superAgencyId.trim()
        const website = payload.website?.trim() || null
        const note = payload.note?.trim() || null

        if (!companyName) return { ok: false, message: '会社名または個人事業主名を入力してください' }
        if (!contactName) return { ok: false, message: '担当者名を入力してください' }
        if (!email) return { ok: false, message: 'メールアドレスを入力してください' }
        if (!phone) return { ok: false, message: '電話番号を入力してください' }
        if (!superAgencyId) return { ok: false, message: '所属する総代理店を選択してください' }

        const { error } = await supabase
          .from('agency_signup_requests')
          .insert({
            company_name: companyName,
            contact_name: contactName,
            email,
            phone,
            super_agency_id: superAgencyId,
            website,
            note,
          })

        if (error) {
          if (error.code === '23505') {
            return { ok: false, message: 'このメールアドレスの申請は審査中です' }
          }
          return { ok: false, message: error.message }
        }

        return { ok: true }
      },

      // --- Admin: org ---
      async adminSetConnectorAgency(connectorUserId, agencyUserId) {
        const { error } = await supabase.rpc('admin_set_connector_agency', {
          p_connector_user_id: connectorUserId,
          p_agency_user_id: agencyUserId,
        })

        if (error) return { ok: false, message: error.message }
        await refresh()
        return { ok: true }
      },

      async adminSetCommissionRates(superAgencyRate, agencyRate, connectorRate) {
        if (!(superAgencyRate >= 0 && superAgencyRate <= 1)) {
          return { ok: false, message: '総代理店報酬率が不正です' }
        }
        if (!(agencyRate >= 0 && agencyRate <= 1)) return { ok: false, message: '代理店報酬率が不正です' }
        if (!(connectorRate >= 0 && connectorRate <= 1)) return { ok: false, message: 'コネクター報酬率が不正です' }
        if (connectorRate > agencyRate) return { ok: false, message: 'コネクター報酬率は代理店報酬率以下にしてください' }
        if (agencyRate > superAgencyRate) return { ok: false, message: '代理店報酬率は総代理店報酬率以下にしてください' }

        const { error } = await supabase
          .from('system_settings')
          .update({
            super_agency_rate: superAgencyRate,
            agency_rate: agencyRate,
            connector_rate: connectorRate,
          })
          .eq('id', 1)

        if (error) return { ok: false, message: error.message }
        await refresh()
        return { ok: true }
      },

      async adminPromoteAgencyToSuperAgency(agencyUserId) {
        const { error } = await supabase.rpc('admin_promote_agency_to_super_agency', {
          p_agency_user_id: agencyUserId,
        })

        if (error) return { ok: false, message: error.message }
        await refresh()
        return { ok: true }
      },

      async adminSetAgencySuperAgency(agencyUserId, superAgencyId) {
        const { error } = await supabase.rpc('admin_set_agency_super_agency', {
          p_agency_user_id: agencyUserId,
          p_super_agency_id: superAgencyId ?? null,
        })

        if (error) return { ok: false, message: error.message }
        await refresh()
        return { ok: true }
      },

      async adminSetUserCommissionOverride(payload) {
        if (!payload.userId) return { ok: false, message: '対象ユーザーを選択してください' }
        if (
          payload.agencyRateOverride !== undefined &&
          payload.agencyRateOverride !== null &&
          !(payload.agencyRateOverride >= 0 && payload.agencyRateOverride <= 1)
        ) {
          return { ok: false, message: '代理店報酬率が不正です' }
        }
        if (
          payload.connectorRateOverride !== undefined &&
          payload.connectorRateOverride !== null &&
          !(payload.connectorRateOverride >= 0 && payload.connectorRateOverride <= 1)
        ) {
          return { ok: false, message: 'コネクター報酬率が不正です' }
        }

        const { error } = await supabase.rpc('admin_set_user_commission_override', {
          p_user_id: payload.userId,
          p_agency_rate_override: payload.agencyRateOverride ?? null,
          p_connector_rate_override: payload.connectorRateOverride ?? null,
        })

        if (error) return { ok: false, message: error.message }
        await refresh()
        return { ok: true }
      },

      async adminDeletePartnerAccount(userId) {
        if (!userId) return { ok: false, message: '対象ユーザーを選択してください' }

        const { error } = await supabase.rpc('admin_delete_partner_account', {
          p_user_id: userId,
        })

        if (error) return { ok: false, message: error.message }
        await refresh()
        return { ok: true }
      },

      async adminReviewAgencySignupRequest(requestId, decision, reviewNote) {
        const { error } = await supabase.rpc('admin_review_agency_signup_request', {
          p_request_id: requestId,
          p_decision: decision,
          p_review_note: reviewNote?.trim() || null,
        })

        if (error) return { ok: false, message: error.message }
        await refresh()
        return { ok: true }
      },

      async adminIssueAgencyAccount(requestId) {
        const { data, error } = await supabase.rpc('admin_issue_agency_account', {
          p_request_id: requestId,
        })
        if (error) return { ok: false, message: error.message }

        const row = Array.isArray(data) ? data[0] : data
        await refresh()
        return {
          ok: true,
          agencyUserId: row?.user_id ?? undefined,
          email: row?.email ?? undefined,
          inviteCode: row?.invite_code ?? undefined,
        }
      },

      async adminSendAgencyApprovalNotification(requestId) {
        const { data: requestRow, error: requestError } = await supabase
          .from('agency_signup_requests')
          .select('email, issued_user_id')
          .eq('id', requestId)
          .single()

        if (requestError) return { ok: false, message: requestError.message }

        const email = safeEmail(requestRow?.email ?? '')
        if (!email) return { ok: false, message: '通知先メールアドレスが見つかりません' }
        if (!requestRow?.issued_user_id) return { ok: false, message: 'アカウント発行後に通知してください' }

        const { error: authError } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: getPasswordResetRedirectUrl(),
        })
        if (authError) {
          if (authError.message.includes('Email address not authorized')) {
            return {
              ok: false,
              message:
                'パスワード設定メールを送信できませんでした。Supabase Auth の SMTP 設定、または許可済み送信先を確認してください。',
            }
          }
          return { ok: false, message: authError.message }
        }

        const { error: logError } = await supabase.rpc('admin_send_agency_approval_notification', {
          p_request_id: requestId,
        })
        if (logError) return { ok: false, message: logError.message }

        await refresh()
        return { ok: true }
      },

      // --- Products ---
      async createProduct(payload) {
        const normalizedName = payload.name.trim()
        const inferredType = payload.type ?? inferProductTypeFromName(normalizedName)
        const listPriceJPY = Math.max(0, Math.floor(payload.listPriceJPY || 0))

        if (listPriceJPY <= 0) {
          return { ok: false, message: '商品金額は 1 円以上で設定してください' }
        }

        const { data, error } = await supabase
          .from('products')
          .insert({
            supplier_id: payload.supplierId,
            name: normalizedName,
            category: payload.category ?? null,
            type: inferredType,
            price_label: null,
            list_price_jpy: listPriceJPY,
            image_url: payload.imageUrl ?? null,
            description: payload.description.trim(),
            materials: payload.materials ?? [],
            is_public: payload.isPublic,
            agency_rate: payload.agencyRate ?? null,
            connector_rate: payload.connectorRate ?? null,
            ad_spec: payload.adSpec ?? null,
            vacancy_status: payload.vacancyStatus ?? null,
          })
          .select('id')
          .single()

        if (error) return { ok: false, message: error.message }
        await refresh()
        return { ok: true, productId: data.id }
      },

      async updateProduct(productId, patch) {
        const updateData: Record<string, any> = {}
        if (patch.name !== undefined) {
          updateData.name = patch.name.trim()
          if (patch.type === undefined) updateData.type = inferProductTypeFromName(patch.name)
        }
        if (patch.category !== undefined) updateData.category = patch.category ?? null
        if (patch.type !== undefined) updateData.type = patch.type
        if (patch.priceLabel !== undefined) {
          updateData.price_label = null
        }
        if (patch.listPriceJPY !== undefined) {
          const listPriceJPY = Math.max(0, Math.floor(patch.listPriceJPY || 0))
          if (listPriceJPY <= 0) {
            return { ok: false, message: '商品金額は 1 円以上で設定してください' }
          }
          updateData.list_price_jpy = listPriceJPY
          updateData.price_label = null
        }
        if (patch.imageUrl !== undefined) updateData.image_url = patch.imageUrl
        if (patch.description !== undefined) updateData.description = patch.description.trim()
        if (patch.materials !== undefined) updateData.materials = patch.materials
        if (patch.isPublic !== undefined) updateData.is_public = patch.isPublic
        if (patch.agencyRate !== undefined) updateData.agency_rate = patch.agencyRate ?? null
        if (patch.connectorRate !== undefined) updateData.connector_rate = patch.connectorRate ?? null
        if (patch.adSpec !== undefined) updateData.ad_spec = patch.adSpec
        if (patch.vacancyStatus !== undefined) updateData.vacancy_status = patch.vacancyStatus

        const { error } = await supabase
          .from('products')
          .update(updateData)
          .eq('id', productId)

        if (error) return { ok: false, message: error.message }
        await refresh()
        return { ok: true }
      },

      async deleteProduct(productId) {
        if (!productId) return { ok: false, message: '商品IDが不正です' }

        const { error } = await supabase.rpc('admin_delete_product', {
          p_product_id: productId,
        })

        if (error) return { ok: false, message: error.message }
        await refresh()
        return { ok: true }
      },

      // --- Deals ---
      async createDealFromReferral(payload) {
        const { data, error } = await supabase.rpc('create_referral_deal', {
          p_connector_id: payload.connectorId,
          p_product_id: payload.productId,
          p_customer_company_name: payload.customerCompanyName,
          p_customer_name: payload.customerName,
          p_customer_email: payload.customerEmail,
          p_customer_phone: payload.customerPhone ?? null,
          p_memo: payload.memo ?? null,
        })

        if (error) return { ok: false, message: error.message }
        await refresh()
        return { ok: true, dealId: data }
      },

      async createDealManual(payload) {
        const productId = payload.productId
        const customerCompanyName = payload.customerCompanyName.trim()
        const customerName = payload.customerName.trim()
        const customerEmail = safeEmail(payload.customerEmail)
        const customerPhone = payload.customerPhone?.trim() || null
        const memo = payload.memo?.trim() || null

        if (!productId) return { ok: false, message: '商材を選択してください' }
        if (!customerName) return { ok: false, message: '顧客担当者名を入力してください' }
        if (!customerEmail) return { ok: false, message: '顧客メールアドレスを入力してください' }

        const { data: manualDealId, error: manualDealError } = await supabase.rpc('create_manual_deal', {
          p_product_id: productId,
          p_customer_company_name: customerCompanyName || null,
          p_customer_name: customerName,
          p_customer_email: customerEmail,
          p_customer_phone: customerPhone,
          p_memo: memo,
          p_assigned_agency_id: payload.assignedAgencyId ?? null,
        })

        if (manualDealError) return { ok: false, message: manualDealError.message }
        await refresh()
        return { ok: true, dealId: manualDealId }
      },

      async updateDealStatus(dealId, status) {
        const { error } = await supabase
          .from('deals')
          .update({ status })
          .eq('id', dealId)
          .eq('locked', false)

        if (error) return { ok: false, message: error.message }
        await refresh()
        return { ok: true }
      },

      async finalizeDeal(payload) {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(payload.closingDate)) {
          return { ok: false, message: '日付は YYYY-MM-DD 形式で入力してください' }
        }

        const { data, error } = await supabase.rpc('finalize_deal', {
          p_deal_id: payload.dealId,
          p_final_sale_amount_jpy: payload.finalSaleAmountJPY,
          p_closing_date: payload.closingDate,
        })

        if (error) return { ok: false, message: error.message }
        await refresh()
        return { ok: true, transactionId: data }
      },

      // --- Rewards ---
      async adminConfirmRewardsForTransaction(transactionId) {
        const { error } = await supabase.rpc('admin_confirm_rewards', {
          p_transaction_id: transactionId,
        })
        if (error) return { ok: false, message: error.message }
        await refresh()
        return { ok: true }
      },

      async requestPayoutAll() {
        const { data, error } = await supabase.rpc('request_payout_all')
        if (error) return { ok: false, message: error.message }
        await refresh()
        return { ok: true, payoutRequestId: data }
      },

      async adminMarkPayoutPaid(payoutRequestId) {
        const { error } = await supabase.rpc('admin_mark_payout_paid', {
          p_payout_request_id: payoutRequestId,
        })
        if (error) return { ok: false, message: error.message }
        await refresh()
        return { ok: true }
      },
    }
  }, [refresh, sessionUserId])

  const value: DbContextValue = useMemo(() => ({ db, loading, actions }), [db, loading, actions])

  return <DbContext.Provider value={value}>{props.children}</DbContext.Provider>
}

export function useDb(): DbContextValue {
  const ctx = useContext(DbContext)
  if (!ctx) throw new Error('DbProvider 縺瑚ｦ九▽縺九ｊ縺ｾ縺帙ｓ')
  return ctx
}

export function getSessionUser(db: Db): User | null {
  if (!db.sessionUserId) return null
  const user = db.users.find((u) => u.id === db.sessionUserId)
  return user && !user.deletedAt ? user : null
}

export function getRoleHomePath(user: User): string {
  if (user.role === 'J-Navi管理者') return '/admin'
  return '/dashboard'
}

